import { useState, useRef, useEffect, useCallback } from 'react'
import { calculateAssessment, type AssessmentInput, type AssessmentResult, type SelfReport } from '../utils/assessmentAlgorithm'
import { sendNotifications, sendBookingNotification } from '../utils/notify'

/* ──────────── 场景数据 ──────────── */
const SCENARIOS = [
  { icon: '🔄', name: '团队重组/合并', desc: '组织架构调整期' },
  { icon: '📈', name: '业绩冲刺高压', desc: '目标压力大' },
  { icon: '🤖', name: 'AI业务重构', desc: '技术变革转型' },
  { icon: '👋', name: '重大人事变动', desc: '核心人员流动' },
  { icon: '⚡', name: '品牌/公共危机', desc: '外部风险冲击' },
  { icon: '🌱', name: '组织文化重塑', desc: '文化升级迭代' },
]

const INDUSTRIES = ['互联网', '制造', '金融', '医药/生物', '零售/消费', '其他']
const TEAM_SIZES = ['50人以下', '50-200人', '200-1000人', '1000人以上']

/* ──────────── 滑动条组件 ──────────── */
function Slider({ label, min, max, step, value, onChange, leftLabel, rightLabel }: {
  label: string; min: number; max: number; step: number; value: number
  onChange: (v: number) => void; leftLabel: string; rightLabel: string
}) {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-2">
        <label style={{ fontSize: '0.8rem', color: '#2c2420', fontWeight: 500 }}>{label}</label>
        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#E8751A', minWidth: '2rem', textAlign: 'right' }}>{value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: '#E8751A', height: '6px', cursor: 'pointer' }}
      />
      <div className="flex justify-between mt-1">
        <span style={{ fontSize: '0.68rem', color: '#a08e78' }}>{leftLabel}</span>
        <span style={{ fontSize: '0.68rem', color: '#a08e78' }}>{rightLabel}</span>
      </div>
    </div>
  )
}

/* ──────────── 雷达图组件（双轨：最终分+基准+自述分） ──────────── */
function RadarChart({ result }: { result: AssessmentResult }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = Math.min(window.devicePixelRatio, 2)
    const size = 340
    canvas.width = size * dpr
    canvas.height = size * dpr
    ctx.scale(dpr, dpr)

    const cx = size / 2
    const cy = size / 2
    const radius = 105
    const labels = ['凝聚力资本', '抗逆力资本', '成长力资本']
    const finalScores = [result.cohesion.finalScore, result.resilience.finalScore, result.growth.finalScore]
    const benchmarks = [result.cohesion.benchmark, result.resilience.benchmark, result.growth.benchmark]
    const selfScores = [result.cohesion.selfScore, result.resilience.selfScore, result.growth.selfScore]
    const maxVal = 90

    ctx.clearRect(0, 0, size, size)

    // 网格
    for (let i = 1; i <= 3; i++) {
      const r = (radius / 3) * i
      ctx.beginPath()
      for (let j = 0; j < 3; j++) {
        const angle = (Math.PI * 2 * j) / 3 - Math.PI / 2
        const x = cx + Math.cos(angle) * r
        const y = cy + Math.sin(angle) * r
        if (j === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.closePath()
      ctx.strokeStyle = i === 3 ? '#c8bfb0' : '#e8e0d4'
      ctx.lineWidth = 1
      ctx.stroke()
    }

    // 轴线
    for (let j = 0; j < 3; j++) {
      const angle = (Math.PI * 2 * j) / 3 - Math.PI / 2
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.lineTo(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius)
      ctx.strokeStyle = '#e8e0d4'
      ctx.lineWidth = 1
      ctx.stroke()
    }

    // 行业基准（虚线灰色）
    const bPoints = benchmarks.map((v, i) => {
      const angle = (Math.PI * 2 * i) / 3 - Math.PI / 2
      const r = (v / maxVal) * radius
      return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r }
    })
    ctx.beginPath()
    bPoints.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))
    ctx.closePath()
    ctx.setLineDash([4, 4])
    ctx.strokeStyle = '#b0b8c4'
    ctx.lineWidth = 1.5
    ctx.stroke()
    ctx.setLineDash([])

    // 最终得分（实线橙色）
    const fPoints = finalScores.map((v, i) => {
      const angle = (Math.PI * 2 * i) / 3 - Math.PI / 2
      const r = (v / maxVal) * radius
      return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r }
    })
    ctx.beginPath()
    fPoints.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))
    ctx.closePath()
    ctx.fillStyle = 'rgba(232, 117, 26, 0.1)'
    ctx.fill()
    ctx.strokeStyle = '#E8751A'
    ctx.lineWidth = 2.5
    ctx.stroke()

    // 顶点圆点
    fPoints.forEach(p => {
      ctx.beginPath()
      ctx.arc(p.x, p.y, 5, 0, Math.PI * 2)
      ctx.fillStyle = '#E8751A'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.stroke()
    })

    // 自述分（蓝色圆点，在轴线上单独标注）
    const selfOn90 = selfScores.map(s => (s / 10) * 90)
    const sPoints = selfOn90.map((v, i) => {
      const angle = (Math.PI * 2 * i) / 3 - Math.PI / 2
      const r = (v / maxVal) * radius
      return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r }
    })
    sPoints.forEach(p => {
      ctx.beginPath()
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2)
      ctx.fillStyle = '#5B8DEF'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 1.5
      ctx.stroke()
    })

    // 标签
    labels.forEach((label, i) => {
      const angle = (Math.PI * 2 * i) / 3 - Math.PI / 2
      const lx = cx + Math.cos(angle) * (radius + 32)
      const ly = cy + Math.sin(angle) * (radius + 32)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.font = '12px "Noto Sans SC", sans-serif'
      ctx.fillStyle = '#5a4a3a'
      ctx.fillText(label, lx, ly - 14)
      ctx.font = 'bold 11px "Noto Sans SC", sans-serif'
      ctx.fillStyle = '#E8751A'
      ctx.fillText(`${finalScores[i]}`, lx, ly)
      ctx.font = '10px "Noto Sans SC", sans-serif'
      ctx.fillStyle = '#5B8DEF'
      ctx.fillText(`自评${selfScores[i]}`, lx, ly + 12)
      ctx.font = '10px "Noto Sans SC", sans-serif'
      ctx.fillStyle = '#999'
      ctx.fillText(`基准${benchmarks[i]}`, lx, ly + 24)
    })
  }, [result])

  return <canvas ref={canvasRef} style={{ width: 340, height: 340 }} />
}

/* ──────────── 主页面 ──────────── */
export default function AssessmentPage() {
  const [step, setStep] = useState<'hero' | 'form' | 'result'>('hero')
  const [formStep, setFormStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<AssessmentResult | null>(null)
  const [notifyStatus, setNotifyStatus] = useState<{ email: boolean; wecom: boolean } | null>(null)
  const [bookingSent, setBookingSent] = useState(false)
  const [formData, setFormData] = useState<Partial<AssessmentInput>>({})
  const [selectedScenes, setSelectedScenes] = useState<{ name: string; rank: number }[]>([])
  const [showToast, setShowToast] = useState(false)
  const [selfReport, setSelfReport] = useState<SelfReport>({
    cohesionScore: 5, resiliencePercent: 50, resilienceScore: 5,
    growthAcceptance: 5, growthResistanceRaw: 5, growthResistanceScore: 6,
    growthInitiativePercent: 50, growthInitiativeScore: 5,
  })
  const formRef = useRef<HTMLDivElement>(null)
  const resultRef = useRef<HTMLDivElement>(null)

  const updateForm = useCallback((key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }))
  }, [])

  // 场景多选+排序
  const handleSceneToggle = (name: string) => {
    setSelectedScenes(prev => {
      const existing = prev.find(s => s.name === name)
      if (existing) {
        const filtered = prev.filter(s => s.name !== name)
        return filtered.map((s, idx) => ({ ...s, rank: idx + 1 }))
      }
      if (prev.length >= 3) { setShowToast(true); setTimeout(() => setShowToast(false), 2000); return prev }
      return [...prev, { name, rank: prev.length + 1 }]
    })
  }

  const getRankBadge = (name: string) => {
    const s = selectedScenes.find(s => s.name === name)
    if (!s) return null
    return ['①', '②', '③'][s.rank - 1]
  }

  const isSceneSelected = (name: string) => selectedScenes.some(s => s.name === name)

  // 提交测评
  const handleSubmit = () => {
    if (!formData.industry || !formData.teamSize || !formData.contactName || !formData.companyName ||
        !formData.foundedYear || !formData.annualRevenue || !formData.phone || !formData.email ||
        selectedScenes.length === 0) return
    setLoading(true)
    setTimeout(() => {
      const input: AssessmentInput = {
        scenes: selectedScenes,
        industry: formData.industry!,
        teamSize: formData.teamSize!,
        companyName: formData.companyName!,
        contactName: formData.contactName!,
        foundedYear: formData.foundedYear!,
        annualRevenue: formData.annualRevenue!,
        phone: formData.phone!,
        email: formData.email!,
        selfReport,
      }
      const res = calculateAssessment(input)
      setResult(res)
      setLoading(false)
      setStep('result')
      const now = new Date()
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
      sendNotifications({
        companyName: res.companyName, contactName: res.contactName, phone: res.phone, email: res.email,
        scenes: res.scenes, industry: res.industry, teamSize: res.teamSize,
        cohesion: res.cohesion.finalScore, resilience: res.resilience.finalScore, growth: res.growth.finalScore,
        overallIndex: res.overallIndex, weakestDimension: res.weakestDimension, riskLevel: res.riskLevel, timestamp,
      }).then(setNotifyStatus)
      setTimeout(() => { resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }, 100)
    }, 4500)
  }

  return (
    <div style={{ background: '#fcfcfc', minHeight: '100vh', fontFamily: '"Noto Sans SC", sans-serif' }}>

      {/* ════════════ 屏1：英雄区 ════════════ */}
      <section className="relative flex flex-col items-center justify-center text-center px-6" style={{ minHeight: '100vh', background: 'linear-gradient(175deg, #e8d8c4 0%, #ede0cc 15%, #f0e6d4 40%, #f3ebdc 60%, #ede0cc 85%, #e8d8c4 100%)' }}>
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(26, 42, 74, 0.03) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(232, 117, 26, 0.04) 0%, transparent 40%)', zIndex: 1 }} />
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(26,42,74,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(26,42,74,0.03) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 1 }} />
        <div className="relative z-10 max-w-2xl">
          <p className="mb-4" style={{ fontSize: '0.72rem', color: '#a08e78', letterSpacing: '0.12em' }}>孔隙心理 — 组织心力资本研究中心</p>
          <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', fontWeight: 700, lineHeight: 1.2, color: '#1A2A4A', wordBreak: 'keep-all', textShadow: '1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.2), 3px 4px 8px rgba(180,160,130,0.12)' }}>
            AI正在重写游戏规则
          </h1>
          <p style={{ fontSize: 'clamp(1.45rem, 3.2vw, 2.6rem)', fontWeight: 700, lineHeight: 1.25, color: '#E8751A', marginTop: 4, wordBreak: 'keep-all', textShadow: '1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.2), 3px 4px 8px rgba(180,160,130,0.12)' }}>
            你的团队，心力资本跟得上吗？
          </p>
          <div className="mt-6 mx-auto text-center" style={{ maxWidth: 480 }}>
            <p style={{ fontSize: '0.82rem', color: '#a08e78', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>获取专属</p>
            <p style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 800, lineHeight: 1.3, color: '#1A2A4A', wordBreak: 'keep-all', letterSpacing: '0.02em', textShadow: '0 1px 2px rgba(0,0,0,0.04)', marginBottom: '1rem' }}>
              《团队心力资本初评》
            </p>
            <div className="flex items-center justify-center gap-2">
              <span style={{ fontSize: 'clamp(1.1rem, 2.2vw, 1.45rem)', fontWeight: 800, color: '#D94A4A', wordBreak: 'keep-all' }}>评测团队战斗力</span>
              <span style={{ fontSize: '1rem', color: '#d4c4b0', fontWeight: 300 }}>|</span>
              <span style={{ fontSize: 'clamp(1.1rem, 2.2vw, 1.45rem)', fontWeight: 800, color: '#E8751A', wordBreak: 'keep-all' }}>看清底牌</span>
            </div>
          </div>
          <p className="mt-5 mx-auto max-w-sm" style={{ fontSize: '0.75rem', lineHeight: 1.7, color: '#a08e78', letterSpacing: '0.03em' }}>
            3分钟 · 多场景复合评估 · 算法+自评双轨融合
          </p>
          <button className="mt-8 px-10 py-3.5 transition-all duration-300" style={{ background: 'transparent', color: '#1A2A4A', borderRadius: '9999px', fontSize: '0.9rem', fontWeight: 600, letterSpacing: '0.1em', cursor: 'pointer', border: '1.5px solid #1A2A4A', position: 'relative', zIndex: 10 }}
            onClick={() => { const el = document.getElementById('scenario-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }) }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1A2A4A'; e.currentTarget.style.color = '#fff' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#1A2A4A' }}>
            开始测评 →
          </button>
          <p className="mt-4" style={{ fontSize: '0.72rem', color: '#a08e78' }}>已有 <strong style={{ color: '#E8751A' }}>1,200+</strong> 家企业管理者完成测评</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '1px', background: 'linear-gradient(90deg, transparent 5%, rgba(160,142,120,0.25) 50%, transparent 95%)' }} />
      </section>

      {/* ════════════ 屏2+3：测评表单区 ════════════ */}
      <section id="scenario-section" ref={formRef} className="relative flex flex-col items-center px-6 py-24" style={{ background: '#fcfcfc', minHeight: '100vh' }}>
        <div className="max-w-lg w-full">

          {/* 进度条 */}
          <div className="flex items-center justify-center gap-2 mb-10">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-2">
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: formStep >= i ? '#E8751A' : '#e8e0d4', color: formStep >= i ? '#fff' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.3s' }}>{i}</div>
                {i < 3 && <div style={{ width: 40, height: 2, background: formStep > i ? '#E8751A' : '#e8e0d4', transition: 'all 0.3s' }} />}
              </div>
            ))}
          </div>

          {/* Step 1：多场景选择 */}
          {formStep === 1 && (
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1A2A4A', textAlign: 'center', marginBottom: '0.5rem' }}>当前面临的核心挑战</h3>
              <p style={{ fontSize: '0.78rem', color: '#a08e78', textAlign: 'center', marginBottom: '1.5rem' }}>选1-3个你在应对的挑战，并按重要性排序</p>
              {selectedScenes.length > 0 && (
                <div className="flex justify-center gap-2 mb-4 flex-wrap">
                  {selectedScenes.sort((a, b) => a.rank - b.rank).map(s => (
                    <span key={s.name} style={{ fontSize: '0.72rem', padding: '3px 10px', borderRadius: '999px', background: '#E8751A', color: '#fff', fontWeight: 500 }}>
                      {['①', '②', '③'][s.rank - 1]} {s.name}
                    </span>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {SCENARIOS.map(s => {
                  const selected = isSceneSelected(s.name)
                  const badge = getRankBadge(s.name)
                  return (
                    <button key={s.name} className="relative py-4 px-3 transition-all duration-200"
                      style={{ background: selected ? '#fff8f3' : '#fff', color: '#2c2420', border: `2px solid ${selected ? '#E8751A' : '#e8e0d4'}`, borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer', lineHeight: 1.5 }}
                      onClick={() => handleSceneToggle(s.name)}>
                      {badge && <span className="absolute" style={{ top: -8, left: -8, width: 22, height: 22, borderRadius: '50%', background: '#E8751A', color: '#fff', fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(232,117,26,0.4)' }}>{badge}</span>}
                      <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '4px' }}>{s.icon}</span>
                      <span style={{ fontWeight: 600 }}>{s.name}</span>
                      <span style={{ fontSize: '0.68rem', color: '#a08e78', display: 'block', marginTop: '2px' }}>{s.desc}</span>
                    </button>
                  )
                })}
              </div>
              <button className="w-full mt-6 py-3.5 transition-all duration-300 hover:scale-[1.02]"
                style={{ background: selectedScenes.length > 0 ? '#E8751A' : '#e8e0d4', color: selectedScenes.length > 0 ? '#fff' : '#999', borderRadius: '9999px', fontSize: '0.88rem', fontWeight: 600, cursor: selectedScenes.length > 0 ? 'pointer' : 'not-allowed', boxShadow: selectedScenes.length > 0 ? '0 4px 16px rgba(232,117,26,0.3)' : 'none' }}
                onClick={() => selectedScenes.length > 0 && setFormStep(2)} disabled={selectedScenes.length === 0}>下一步</button>
            </div>
          )}

          {/* Step 2：基本信息 */}
          {formStep === 2 && (
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1A2A4A', textAlign: 'center', marginBottom: '1.5rem' }}>基本信息</h3>
              {/* 姓名 */}
              <div className="mb-4">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>您的姓名 *</label>
                <input type="text" placeholder="如何称呼您" className="w-full py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.85rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.contactName || ''} onChange={e => updateForm('contactName', e.target.value)} />
              </div>
              {/* 公司名称 */}
              <div className="mb-4">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>公司名称 *</label>
                <input type="text" placeholder="用于生成报告抬头" className="w-full py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.85rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.companyName || ''} onChange={e => updateForm('companyName', e.target.value)} />
              </div>
              {/* 行业 */}
              <div className="mb-4">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>所属行业 *</label>
                <select className="w-full py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.85rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.industry || ''} onChange={e => updateForm('industry', e.target.value)}>
                  <option value="">请选择行业</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              {/* 团队规模 */}
              <div className="mb-4">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>团队规模 *</label>
                <div className="flex flex-wrap gap-2">
                  {TEAM_SIZES.map(size => (
                    <button key={size} className="py-2 px-3 transition-all duration-200"
                      style={{ background: formData.teamSize === size ? '#1A2A4A' : '#fff', color: formData.teamSize === size ? '#fff' : '#2c2420', border: `1.5px solid ${formData.teamSize === size ? '#1A2A4A' : '#e8e0d4'}`, borderRadius: '8px', fontSize: '0.78rem', cursor: 'pointer' }}
                      onClick={() => updateForm('teamSize', size)}>{size}</button>
                  ))}
                </div>
              </div>
              {/* 成立时间 */}
              <div className="mb-4">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>企业成立时间 *</label>
                <input type="text" placeholder="如：2018年" className="w-full py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.85rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.foundedYear || ''} onChange={e => updateForm('foundedYear', e.target.value)} />
              </div>
              {/* 年产值 */}
              <div className="mb-4">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>上一年年产值 *</label>
                <input type="text" placeholder="如：5000万" className="w-full py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.85rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.annualRevenue || ''} onChange={e => updateForm('annualRevenue', e.target.value)} />
              </div>
              {/* 手机号 */}
              <div className="mb-4">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>手机号 *</label>
                <input type="tel" placeholder="输入手机号" className="w-full py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.85rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.phone || ''} onChange={e => updateForm('phone', e.target.value)} />
              </div>
              {/* 邮箱 */}
              <div className="mb-6">
                <label style={{ fontSize: '0.78rem', color: '#6e5e50', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>工作邮箱 *</label>
                <input type="email" placeholder="输入邮箱地址" className="w-full py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.85rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.email || ''} onChange={e => updateForm('email', e.target.value)} />
              </div>
              <button className="w-full py-3.5 transition-all duration-300 hover:scale-[1.02] mb-3"
                style={{ background: formData.industry && formData.teamSize && formData.contactName && formData.companyName ? '#E8751A' : '#e8e0d4', color: formData.industry && formData.teamSize && formData.contactName && formData.companyName ? '#fff' : '#999', borderRadius: '9999px', fontSize: '0.88rem', fontWeight: 600, cursor: formData.industry && formData.teamSize && formData.contactName && formData.companyName ? 'pointer' : 'not-allowed', boxShadow: formData.industry && formData.teamSize && formData.contactName && formData.companyName ? '0 4px 16px rgba(232,117,26,0.3)' : 'none' }}
                onClick={() => formData.industry && formData.teamSize && formData.contactName && formData.companyName && setFormStep(3)} disabled={!formData.industry || !formData.teamSize || !formData.contactName || !formData.companyName}>
                下一步：团队现状自评 →
              </button>
              <button className="w-full mt-3 py-2" style={{ background: 'transparent', color: '#a08e78', fontSize: '0.75rem', border: 'none', cursor: 'pointer' }} onClick={() => setFormStep(1)}>← 上一步</button>
            </div>
          )}

          {/* ════════════════════════════════════════════
              Step 2.5：团队现状自评（新增）
          ════════════════════════════════════════════ */}
          {formStep === 3 && (
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1A2A4A', textAlign: 'center', marginBottom: '0.5rem' }}>团队现状自评</h3>
              <p style={{ fontSize: '0.78rem', color: '#a08e78', textAlign: 'center', marginBottom: '1.5rem' }}>
                以下问题用于校准报告精准度，请根据您的真实感受作答。
              </p>

              {/* 凝聚力自评 */}
              <div className="mb-6 p-4" style={{ background: '#faf9f7', borderRadius: '12px', border: '1px solid #e8e0d4' }}>
                <div className="flex items-center gap-2 mb-1">
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: '#1A2A4A', padding: '2px 8px', borderRadius: '4px' }}>凝聚力</div>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#2c2420', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                  在{selectedScenes.length > 0 ? `「${selectedScenes.sort((a,b)=>a.rank-b.rank)[0].name}」` : '当前挑战'}推进过程中，各部门信息共享、目标一致的程度如何？
                </p>
                <Slider label="" min={1} max={10} step={1} value={selfReport.cohesionScore}
                  onChange={v => setSelfReport(prev => ({ ...prev, cohesionScore: v }))}
                  leftLabel="1 = 各说各话/严重割裂" rightLabel="10 = 高度一致/顺畅协作" />
              </div>

              {/* 抗逆力自评 */}
              <div className="mb-6 p-4" style={{ background: '#faf9f7', borderRadius: '12px', border: '1px solid #e8e0d4' }}>
                <div className="flex items-center gap-2 mb-1">
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: '#3A9B6E', padding: '2px 8px', borderRadius: '4px' }}>抗逆力</div>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#2c2420', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                  遇到困难或不确定性时，团队中主动想办法、积极应对的人占比大约多少？
                </p>
                <Slider label="" min={0} max={100} step={10} value={selfReport.resiliencePercent}
                  onChange={v => setSelfReport(prev => ({ ...prev, resiliencePercent: v, resilienceScore: v / 10 }))}
                  leftLabel="0% = 无人主动" rightLabel="100% = 全员积极应对" />
                <p style={{ fontSize: '0.72rem', color: '#a08e78', marginTop: '0.25rem' }}>折算自述分：{Math.round(selfReport.resilienceScore)} / 10</p>
              </div>

              {/* 成长力自评（3个子项） */}
              <div className="mb-6 p-4" style={{ background: '#faf9f7', borderRadius: '12px', border: '1px solid #e8e0d4' }}>
                <div className="flex items-center gap-2 mb-1">
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: '#E8751A', padding: '2px 8px', borderRadius: '4px' }}>成长力</div>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#2c2420', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                  以下3项请分别评估：
                </p>
                {/* 子项1：接受速度 */}
                <div className="mb-3 pl-3" style={{ borderLeft: '2px solid #e8e0d4' }}>
                  <p style={{ fontSize: '0.78rem', color: '#6e5e50', marginBottom: '0.5rem' }}>① 老员工对新工具/新流程的接受速度</p>
                  <Slider label="" min={1} max={10} step={1} value={selfReport.growthAcceptance}
                    onChange={v => setSelfReport(prev => ({ ...prev, growthAcceptance: v }))}
                    leftLabel="1 = 非常抗拒" rightLabel="10 = 快速接受" />
                </div>
                {/* 子项2：抵触情绪（反向计分） */}
                <div className="mb-3 pl-3" style={{ borderLeft: '2px solid #e8e0d4' }}>
                  <p style={{ fontSize: '0.78rem', color: '#6e5e50', marginBottom: '0.5rem' }}>② 老员工对变革的抵触情绪程度（反向计分）</p>
                  <Slider label="" min={1} max={10} step={1} value={selfReport.growthResistanceRaw}
                    onChange={v => setSelfReport(prev => ({ ...prev, growthResistanceRaw: v, growthResistanceScore: 11 - v }))}
                    leftLabel="1 = 非常抵触（反向得10分）" rightLabel="10 = 毫无抵触（反向得1分）" />
                  <p style={{ fontSize: '0.72rem', color: '#E8751A', marginTop: '0.25rem' }}>反向折算：{selfReport.growthResistanceRaw} → {selfReport.growthResistanceScore} / 10</p>
                </div>
                {/* 子项3：主动学习占比 */}
                <div className="mb-2 pl-3" style={{ borderLeft: '2px solid #e8e0d4' }}>
                  <p style={{ fontSize: '0.78rem', color: '#6e5e50', marginBottom: '0.5rem' }}>③ 主动学习新工具/新流程的人占团队比例</p>
                  <Slider label="" min={0} max={100} step={10} value={selfReport.growthInitiativePercent}
                    onChange={v => setSelfReport(prev => ({ ...prev, growthInitiativePercent: v, growthInitiativeScore: v / 10 }))}
                    leftLabel="0% = 无人主动" rightLabel="100% = 全员主动" />
                </div>
                <p style={{ fontSize: '0.72rem', color: '#a08e78', marginTop: '0.5rem' }}>
                  成长力自述分 = ({selfReport.growthAcceptance} + {selfReport.growthResistanceScore} + {Math.round(selfReport.growthInitiativeScore)}) / 3 = {Math.round((selfReport.growthAcceptance + selfReport.growthResistanceScore + selfReport.growthInitiativeScore) / 3)}
                </p>
              </div>

              {/* 生成报告按钮 */}
              <button className="w-full py-3.5 transition-all duration-300 hover:scale-[1.02] mb-3"
                style={{
                  background: 'linear-gradient(180deg, #f08a35 0%, #E8751A 40%, #d46816 100%)',
                  color: '#fff', borderRadius: '9999px', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 0 rgba(0,0,0,0.12), 0 6px 0 #b8560f, 0 8px 16px rgba(184,86,15,0.4)',
                }}
                onClick={handleSubmit}>
                🚀 生成我的《团队心力资本初评》
              </button>
              <p style={{ fontSize: '0.68rem', color: '#aaa', textAlign: 'center', lineHeight: 1.6 }}>
                🔒 提交即代表您同意《隐私政策》| 数据仅用于生成报告，顾问联系不超过2次
              </p>
              <button className="w-full mt-3 py-2" style={{ background: 'transparent', color: '#a08e78', fontSize: '0.75rem', border: 'none', cursor: 'pointer' }} onClick={() => setFormStep(2)}>← 上一步</button>
            </div>
          )}
        </div>
      </section>

      {/* ════════════ 加载动画 ════════════ */}
      {loading && (
        <section className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{ background: 'rgba(252, 252, 252, 0.96)' }}>
          <div className="max-w-sm text-center px-6">
            <div style={{ width: '100%', height: 4, background: '#e8e0d4', borderRadius: 999, overflow: 'hidden', marginBottom: '2rem' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg, #E8751A, #1A2A4A)', borderRadius: 999, animation: 'loadProgress 4.5s ease-in-out forwards' }} />
            </div>
            <div style={{ fontSize: '0.85rem', color: '#1A2A4A', fontWeight: 500, marginBottom: '0.5rem' }}>正在融合算法数据与您的自评...</div>
            <div style={{ fontSize: '0.75rem', color: '#a08e78', lineHeight: 2 }}>
              <p style={{ animation: 'loadFade 4.5s infinite' }}>正在匹配行业基准数据...</p>
              <p style={{ animation: 'loadFade 4.5s 1.5s infinite' }}>正在校准您的感知评分...</p>
              <p style={{ animation: 'loadFade 4.5s 3s infinite' }}>正在生成双轨融合报告...</p>
            </div>
          </div>
          <style>{`
            @keyframes loadProgress { 0% { width: 0%; } 33% { width: 33%; } 66% { width: 66%; } 100% { width: 100%; } }
            @keyframes loadFade { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
          `}</style>
        </section>
      )}

      {/* ════════════ 提示 toast ════════════ */}
      {showToast && (
        <div className="fixed top-20 left-1/2 z-50 px-6 py-3 rounded-lg shadow-lg" style={{ background: '#1A2A4A', color: '#fff', fontSize: '0.85rem', transform: 'translateX(-50%)', animation: 'fadeInDown 0.3s ease-out' }}>
          最多选择3项挑战，请先取消一项再选
        </div>
      )}

      {/* ════════════ 屏4：结果展示区 ════════════ */}
      {step === 'result' && result && (
        <section ref={resultRef} className="relative flex flex-col items-center px-6 py-24" style={{ background: '#fcfcfc', minHeight: '100vh' }}>
          <div className="max-w-2xl w-full">

            {/* 通知状态 */}
            {notifyStatus && (
              <div className="mb-6 p-4 rounded-xl" style={{ background: '#f0f7f0', border: '1px solid #c8e0c8' }}>
                <p style={{ fontSize: '0.82rem', color: '#3A9B6E', textAlign: 'center', fontWeight: 500 }}>✅ 测评结果已生成！报告已通知专属顾问</p>
              </div>
            )}

            {/* 数据来源说明 */}
            <div className="mb-6 p-4" style={{ background: '#f0f4f8', borderRadius: '10px', border: '1px solid #d8dfe8' }}>
              <p style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1A2A4A', marginBottom: '0.5rem' }}>📊 数据来源说明</p>
              <p style={{ fontSize: '0.75rem', color: '#6e5e50', lineHeight: 1.6 }}>
                本报告综合两种数据源生成：● 客观基准（行业+规模常模，权重70%）● 管理者自述（您的主观评估，权重30%）。最终得分为二者加权融合。
              </p>
            </div>

            {/* 报告抬头 */}
            <div className="text-center mb-10">
              <p style={{ fontSize: '0.72rem', color: '#a08e78', letterSpacing: '0.1em' }}>《团队心力资本初评》— 双轨融合诊断报告</p>
              <h2 style={{ fontSize: 'clamp(1.3rem, 3vw, 1.8rem)', fontWeight: 700, color: '#1A2A4A', marginTop: '0.5rem', wordBreak: 'keep-all' }}>{result.companyName}</h2>
              <p style={{ fontSize: '0.78rem', color: '#a08e78', marginTop: '0.5rem' }}>{result.industry} · {result.teamSize}</p>
            </div>

            {/* 综合评定 */}
            {(() => {
              const industryAvg = Math.round((result.cohesion.benchmark + result.resilience.benchmark + result.growth.benchmark) / 3 * 10) / 10
              const ratio = result.overallIndex / industryAvg
              let grade: string, gradeColor: string, gradeBg: string, gradeDesc: string
              if (result.overallIndex >= industryAvg * 1.2) {
                grade = '优秀'; gradeColor = '#3A9B6E'; gradeBg = 'linear-gradient(135deg, #e8f5e9, #c8e6c9)'
                gradeDesc = '团队心力资本充沛，具备较强变革承载力'
              } else if (result.overallIndex < industryAvg) {
                grade = '即刻辅导'; gradeColor = '#D94A4A'; gradeBg = 'linear-gradient(135deg, #ffebee, #ffcdd2)'
                gradeDesc = '团队心力资本低于行业基准，建议尽快启动干预'
              } else {
                grade = '及时辅导'; gradeColor = '#E8751A'; gradeBg = 'linear-gradient(135deg, #fff3e0, #ffe0b2)'
                gradeDesc = '团队心力资本尚可，但仍有提升空间'
              }
              return (
                <div className="mb-8">
                  <div className="text-center py-6" style={{ background: gradeBg, borderRadius: '16px', border: `2px solid ${gradeColor}` }}>
                    <p style={{ fontSize: '0.78rem', color: gradeColor, fontWeight: 500, marginBottom: '0.5rem' }}>综合评定（算法70% + 自评30%）</p>
                    <p style={{ fontSize: '2.8rem', fontWeight: 800, lineHeight: 1.2, color: gradeColor, letterSpacing: '0.05em' }}>{grade}</p>
                    <p style={{ fontSize: '0.72rem', color: gradeColor, opacity: 0.8, marginTop: '0.5rem' }}>{gradeDesc}</p>
                    <div className="flex justify-center gap-4 mt-3" style={{ fontSize: '0.68rem', color: '#888' }}>
                      <span>融合得分：{result.overallIndex}</span>
                      <span>行业基准：{industryAvg}</span>
                      <span>偏差：{ratio >= 1 ? '+' : ''}{Math.round((ratio - 1) * 100)}%</span>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* 雷达图 + 维度解读 */}
            <div className="flex flex-col md:flex-row items-center gap-8 mb-8" style={{ background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid #e8e0d4' }}>
              <div className="flex-shrink-0">
                <RadarChart result={result} />
                {/* 图例 */}
                <div className="flex justify-center gap-4 mt-2" style={{ fontSize: '0.65rem', color: '#888' }}>
                  <span><span style={{ display: 'inline-block', width: 12, height: 3, background: '#E8751A', borderRadius: 2, verticalAlign: 'middle', marginRight: 4 }} />最终得分</span>
                  <span><span style={{ display: 'inline-block', width: 12, height: 3, background: '#b0b8c4', borderRadius: 2, verticalAlign: 'middle', marginRight: 4, backgroundImage: 'repeating-linear-gradient(90deg, #b0b8c4 0, #b0b8c4 3px, transparent 3px, transparent 6px)' }} />行业基准</span>
                  <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#5B8DEF', borderRadius: '50%', verticalAlign: 'middle', marginRight: 4 }} />您的自评</span>
                </div>
              </div>
              <div className="flex-1">
                <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1A2A4A', marginBottom: '1rem' }}>维度解读（双轨融合）</h4>
                {[
                  { label: '凝聚力资本', d: result.cohesion },
                  { label: '抗逆力资本', d: result.resilience },
                  { label: '成长力资本', d: result.growth },
                ].map(item => (
                  <div key={item.label} className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                      <span style={{ fontSize: '0.8rem', color: '#2c2420', fontWeight: 500 }}>{item.label}</span>
                      <span style={{ fontSize: '0.78rem' }}>
                        <strong style={{ color: '#E8751A' }}>{item.d.finalScore}</strong>
                        <span style={{ color: '#5B8DEF', marginLeft: 6 }}>自评{item.d.selfScore}</span>
                        <span style={{ color: '#aaa', marginLeft: 6 }}>基准{item.d.benchmark}</span>
                      </span>
                    </div>
                    <div style={{ height: 6, background: '#f0ebe3', borderRadius: 999, overflow: 'hidden', marginBottom: '0.25rem' }}>
                      <div style={{ height: '100%', width: `${(item.d.finalScore / 90) * 100}%`, background: item.d.finalScore >= item.d.benchmark ? '#3A9B6E' : '#D94A4A', borderRadius: 999, transition: 'width 1s ease-out' }} />
                    </div>
                    {/* 感知差距标签 */}
                    <span style={{
                      fontSize: '0.65rem', padding: '1px 8px', borderRadius: '999px',
                      background: item.d.gapType === 'overestimate' ? '#fff3e0' : item.d.gapType === 'underestimate' ? '#ffebee' : '#e8f5e9',
                      color: item.d.gapType === 'overestimate' ? '#E8751A' : item.d.gapType === 'underestimate' ? '#D94A4A' : '#3A9B6E',
                      fontWeight: 500,
                    }}>
                      {item.d.gapType === 'overestimate' ? '自评偏高' : item.d.gapType === 'underestimate' ? '自评偏低' : '感知一致'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 感知差距诊断 */}
            <div className="mb-8" style={{ background: '#f0f7ff', borderRadius: '12px', padding: '1.5rem', border: '1px solid #c8d8f0' }}>
              <p style={{ fontSize: '0.72rem', fontWeight: 600, color: '#5B8DEF', marginBottom: '0.75rem', letterSpacing: '0.05em' }}>
                📊 管理者感知校准诊断：{result.overallGapType}
              </p>
              <p style={{ fontSize: '0.82rem', color: '#2c2420', lineHeight: 1.7 }}>{result.overallGapDesc}</p>
            </div>

            {/* 风险预警 */}
            <div className="mb-8" style={{ background: '#FDF0F0', borderLeft: '4px solid #D94A4A', borderRadius: '0 12px 12px 0', padding: '1.5rem' }}>
              <p style={{ fontSize: '0.72rem', color: '#D94A4A', fontWeight: 600, marginBottom: '0.5rem' }}>⚠️ 风险预警（{result.riskLevel}风险）</p>
              <p style={{ fontSize: '0.82rem', color: '#5a4a3a', lineHeight: 1.7, whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: result.riskWarning.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#D94A4A">$1</strong>') }} />
              <div className="flex gap-4 mt-3" style={{ fontSize: '0.7rem', color: '#a08e78' }}>
                <span>最大短板：<strong style={{ color: '#E8751A' }}>{result.weakestDimension}</strong></span>
                <span>建议干预窗口：4周内</span>
              </div>
            </div>

            {/* 留资表单 */}
            <div id="lead-form" className="mb-8" style={{ background: '#fff', border: '1.5px solid #E8751A', borderRadius: '16px', padding: '2rem' }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1A2A4A', textAlign: 'center', marginBottom: '0.5rem' }}>简单解读并沟通初评</h4>
              <p style={{ fontSize: '0.75rem', color: '#a08e78', textAlign: 'center', marginBottom: '1.5rem' }}>顾问1对1沟通，含初评理解、需求拆解、服务手册</p>
              <div className="flex flex-col md:flex-row gap-3 mb-4">
                <input type="tel" placeholder="手机号（顾问将短信提醒）" className="flex-1 py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.82rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.phone || ''} onChange={e => updateForm('phone', e.target.value)} />
                <input type="email" placeholder="工作邮箱（接收PDF报告）" className="flex-1 py-2.5 px-4" style={{ border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.82rem', color: '#2c2420', background: '#fff', outline: 'none' }} value={formData.email || ''} onChange={e => updateForm('email', e.target.value)} />
              </div>
              <button className="w-full py-3.5 transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: formData.phone && formData.email ? 'linear-gradient(180deg, #f08a35 0%, #E8751A 40%, #d46816 100%)' : '#e8e0d4',
                  color: formData.phone && formData.email ? '#fff' : '#999', borderRadius: '9999px', fontSize: '0.88rem', fontWeight: 600,
                  cursor: formData.phone && formData.email ? 'pointer' : 'not-allowed',
                  boxShadow: formData.phone && formData.email ? 'inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 0 rgba(0,0,0,0.12), 0 6px 0 #b8560f, 0 8px 16px rgba(184,86,15,0.4)' : 'none',
                }}
                onClick={async () => {
                  if (!formData.phone || !formData.email || !result) return
                  const now = new Date()
                  const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
                  // 1. 发送企微群通知（通知客服有人预约）
                  await sendBookingNotification({ companyName: result.companyName, contactName: result.contactName, phone: formData.phone, email: formData.email, timestamp })
                  // 2. 同时打开企微客服对话窗口
                  window.open('https://work.weixin.qq.com/kfid/kfcc1729b8642e445e3', '_blank')
                  setBookingSent(true)
                }}
                disabled={!formData.phone || !formData.email || bookingSent}>
                {bookingSent ? '✅ 已提交，顾问将尽快联系您' : '🎁 预约顾问免费连线（10分钟）'}
              </button>
              <p style={{ fontSize: '0.65rem', color: '#bbb', textAlign: 'center', marginTop: '0.75rem' }}>🔒 数据仅用于生成报告，顾问联系不超过2次</p>
            </div>
          </div>
        </section>
      )}

      {/* ════════════ 信任背书 ════════════ */}
      {step === 'result' && (
        <section className="px-6 py-16" style={{ background: '#f5f0e8' }}>
          <div className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div><div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>🎓</div><p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1A2A4A', marginBottom: '0.3rem' }}>专业背书</p><p style={{ fontSize: '0.72rem', color: '#6e5e50', lineHeight: 1.6 }}>顾问团队均拥有组织心理学硕士以上学历+10年企业实战经验</p></div>
            <div><div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>🔒</div><p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1A2A4A', marginBottom: '0.3rem' }}>数据安全</p><p style={{ fontSize: '0.72rem', color: '#6e5e50', lineHeight: 1.6 }}>数据加密传输 · 仅用于生成报告 · 可随时删除</p></div>
            <div><div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>👥</div><p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1A2A4A', marginBottom: '0.3rem' }}>社会认同</p><p style={{ fontSize: '0.72rem', color: '#6e5e50', lineHeight: 1.6 }}>已有<strong style={{ color: '#E8751A' }}>1,200+</strong>管理者使用该测评</p></div>
          </div>
        </section>
      )}

      {/* ════════════ Footer ════════════ */}
      {step === 'result' && (
        <section className="px-6 py-16 text-center" style={{ background: '#1A2A4A', color: '#fff' }}>
          <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem', opacity: 0.8 }}>孔隙心理 · 组织心力资本研究中心</p>
          <p style={{ fontSize: '0.72rem', opacity: 0.5, marginBottom: '1.5rem' }}>让AI原生服务融入日常，让团队心力提升组织战斗力</p>
          <a href="#/" style={{ fontSize: '0.78rem', color: '#E8751A', textDecoration: 'none', opacity: 0.8 }}>← 返回孔隙心理官网</a>
        </section>
      )}

      <style>{`
        @keyframes fadeInDown { from { transform: translateX(-50%) translateY(-10px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }
      `}</style>
    </div>
  )
}
